import * as THREE from 'three';
import { UVConfig } from '@blade-sxp/map/config/roiConfig';
/**
 * 矫正坐标点到同一平面，用于获取中心点，平面单位法向量,矫正后的坐标点
 * @param {THREE.Vector3[]} vectorList: 坐标点list
 * @return {THREE.Vector3} center: 中心坐标点
 * @return {THREE.Vector3} normal: 平面单位法向量
 * @return {THREE.Vector3[]} vetices: 矫正后的坐标点
 * */
export const correctVectorsToCommonPlane = (vectorList) => {
    const center = new THREE.Vector3();
    let index = 2;
    let len = vectorList.length;
    const first = vectorList[0];
    const last = vectorList[len - 1];
    const isAdd = JSON.stringify(first) !== JSON.stringify(last); // 判断是否补充最好坐标点
    if (isAdd) {
        vectorList.push(first);
        len++;
    }
    for (let i = index; i < len; i++) {
        // 判断共线情况
        if (Number(new THREE.Vector3().subVectors(vectorList[0], vectorList[1]).dot(new THREE.Vector3().subVectors(vectorList[1], vectorList[index]))) === 0) {
            index++;
        }
        else {
            break;
        }
    }
    const plane = new THREE.Plane().setFromCoplanarPoints(vectorList[0], vectorList[1], vectorList[index]); // 三维空间中的二维平面
    const normal = plane.normalize().normal.clone(); // 平面法向量
    const yPixel = new THREE.Vector3(0, 1, 0);
    const isNegate = yPixel.dot(normal) > 0;
    const vetices = vectorList.map((item) => {
        // 将其他点投影到该平面
        const vector = new THREE.Vector3();
        plane.projectPoint(item, vector);
        center.add(vector);
        return vector;
    });
    const targetNomal = isNegate ? normal : normal.negate();
    return {
        center,
        normal: targetNomal,
        vetices,
    };
};
/**
 * 用于3d小人坐标转化到对应的3d平面上
 * @param { { x: number; y: number }} vertices: 平面坐标点
 * @param { THREE.Vector3} vertices: 平面中心点
 * @param { THREE.Vector3} normalize: 平面法向量
 * @return { THREE.Vector3 } 转化后的坐标点
 * */
export const getPosition = (vertices, center, normalize) => {
    const { x = 0, y = 0, z = 0 } = center;
    // 平面xy对应three中zx
    const xPixel = x - vertices.x;
    const zPixel = z - vertices.y;
    const yPixel = (normalize.z * zPixel + normalize.x * xPixel) / normalize.y + y + 0.15;
    return new THREE.Vector3(vertices.x, yPixel, vertices.y);
};
/**
 * 获取光栅墙的uv和顶点
*/
export const getWallRoiUvPos = (vertices, wallHeight, normalVector3) => {
    if (!wallHeight) {
        return {
            uv: [],
            pos: [],
        };
    }
    const h = wallHeight; // 围墙拉伸高度
    const wallH = normalVector3.clone().multiplyScalar(h);
    const c = [];
    const uvH = UVConfig.wall;
    vertices.forEach((item) => {
        c.push(...[item.x ?? 0, item.y ?? 0, item.z ?? 0]);
    });
    const posArr = [];
    const uvrr = [];
    for (let i = 0; i < c.length - 5; i += 3) {
        // 矩形的三角形
        posArr.push(c[i], c[i + 1], c[i + 2], c[i + 3], c[i + 4], c[i + 5], c[i + 3] + (wallH?.x ?? 0), c[i + 4] + (wallH?.y ?? 0), c[i + 5] + (wallH?.z ?? 0));
        // 矩形的三角形
        posArr.push(c[i], c[i + 1], c[i + 2], c[i + 3] + (wallH?.x ?? 0), c[i + 4] + (wallH?.y ?? 0), c[i + 5] + (wallH?.z ?? 0), c[i] + (wallH?.x ?? 0), c[i + 1] + (wallH?.y ?? 0), c[i + 2] + (wallH?.z ?? 0));
        uvrr.push(0, 0, 1, 0, 1, uvH); // x分量即为底部边， y分量为墙的高度
        uvrr.push(0, 0, 1, uvH, 0, uvH);
    }
    return {
        uv: uvrr,
        pos: posArr,
    };
};
/**
 * 获取border边线的uv以及顶点坐标点阵
*/
export const getBorderParkUvPos = (vertices, borderWidth = 0, normalVector3, parkWidth = 0, parkIndex = 0, isPark = false) => {
    const borderH = normalVector3.clone().multiplyScalar(borderWidth); // border高度
    const posArr = [];
    const uvrr = [];
    const len = vertices.length;
    for (let i = 0; i < len - 1; i++) {
        // 停车位
        let positionH;
        let uvY;
        if (i === parkIndex && isPark) {
            const parkH = normalVector3.clone().multiplyScalar(parkWidth); // border高度
            positionH = parkH;
            uvY = UVConfig.park;
        }
        else {
            positionH = borderH;
            uvY = UVConfig.border;
        }
        posArr.push(...[
            vertices[i].x,
            vertices[i].y,
            vertices[i].z,
            vertices[i + 1].x,
            vertices[i + 1].y,
            vertices[i + 1].z,
            vertices[i + 1].x + positionH.x,
            vertices[i + 1].y + positionH.y,
            vertices[i + 1].z + positionH.z,
        ]);
        posArr.push(...[
            vertices[i].x,
            vertices[i].y,
            vertices[i].z,
            vertices[i + 1].x + positionH.x,
            vertices[i + 1].y + positionH.y,
            vertices[i + 1].z + positionH.z,
            vertices[i].x + positionH.x,
            vertices[i].y + positionH.y,
            vertices[i].z + positionH.z,
        ]);
        uvrr.push(0, uvY, 0, uvY, 0, uvY); // x分量即为底部边， y分量为墙的高度
        uvrr.push(0, uvY, 0, uvY, 0, uvY);
    }
    return {
        uv: uvrr,
        pos: posArr,
    };
};
/**
 * 获取roi三角形点阵以及uv
*/
export const getRoiUvPos = (vertices) => {
    // const vertices: THREE.Vector3[] = this.vertices;
    // 判定是否是凹多边形
    const isAoShape = isConcavePoly(vertices);
    // 把凹多边形转为多个凸多边形
    const shapes = isAoShape
        ? divideConcavePoly(vertices)
        : [vertices];
    const faces = creatTrangleFacesByShapes(shapes);
    const posArr = [];
    const uvrr = [];
    faces.forEach((face) => {
        face.forEach((point) => {
            posArr.push(...[point?.x ?? 0, point?.y ?? 0, point?.z ?? 0]);
        });
        uvrr.push(0, 0, 0, 0, 0, 0);
    });
    return {
        uv: uvrr,
        pos: posArr,
    };
};
// 旋转切割roi
/**
 * 判断是否为凹多边形
 * @param vexs 多边形顶点数组
 */
export const isConcavePoly = (vexs) => {
    // prev: 上两邻边间叉乘结果；cur当前两邻边叉乘结果
    let prev, cur;
    // 遍历所有顶点
    for (let i = 1, len = vexs.length; i < len - 1; i++) {
        // 向量v1 = 当前顶点 - 上一顶点
        const v1 = new THREE.Vector3().subVectors(vexs[i], vexs[i - 1]);
        // 向量v2 = 下一顶点 - 当前顶点
        const v2 = new THREE.Vector3().subVectors(vexs[i + 1], vexs[i]);
        // 计算两邻边向量叉积：若不为负则记为1，为负则记为-1
        // cur = v1.clone().dot(v2) >= 0 ? 1 : -1;
        cur = new THREE.Vector3().crossVectors(v1, v2).y >= 0 ? 1 : -1;
        // 若当前两邻边叉积结果与上两邻边结果不同号，即可判断为凹多边形
        if (prev !== undefined && prev !== cur) {
            return true;
        }
        prev = cur;
    }
    // 不是凹多边形
    return false;
};
/**
 * 将凹多边形分割为多个凸多边形(旋转分割法)
 * @param vexs 多边形顶点
 */
export const divideConcavePoly = (vexs) => {
    // 分割多边形结果集，将拆分出来的多边形保存到这个数组
    let polygonList = [];
    const len = vexs.length;
    let i;
    let j;
    let flag = false;
    // polygon1和polygon2分别保存分割出来的两个多边形，polygon1初始化为原多边形，polygon2为空
    const polygon1 = [...vexs];
    let polygon2 = [];
    // 遍历所有顶点
    for (i = 0; i < len - 2; i++) {
        // 将当前顶点和下一个顶点的连线向量作为x轴
        const vAxis = new THREE.Vector3().subVectors(vexs[i + 1], vexs[i]);
        // 当前顶点和下下顶点的连线向量
        let v = new THREE.Vector3().subVectors(vexs[i + 2], vexs[i]);
        // 若发现下下个顶点在x轴下方
        if (new THREE.Vector3().crossVectors(vAxis, v).y < 0 && i + 3 < len) {
            // 遍历余下的顶点
            for (j = i + 3; j < len; j++) {
                // 找到余下的第一个不在x轴下方的顶点
                v = new THREE.Vector3().subVectors(vexs[j], vexs[i]);
                if (new THREE.Vector3().crossVectors(vAxis, v).y > 0) {
                    // 该点即为分割点。找到分割点即跳出循环
                    flag = true;
                    break;
                }
            }
            if (flag)
                break;
        }
    }
    if (j) {
        // 此时分割多边形的两个点分别为vexs[i + 1]和vexs[j]
        // 保存两个分割点
        const dp1 = polygon1[i + 1];
        const dp2 = polygon1[j];
        // 从原来的多边形按照分割点分割出另一个子多边形保存到polygon2
        polygon2 = polygon1.splice(i + 2, j - (i + 2));
        // 子多边形也要补上分割点
        polygon2.unshift(dp1);
        polygon2.push(dp2);
        // 将两个子多边形加入到分割多边形结果集
        polygonList.push(polygon1);
        polygonList.push(polygon2);
    }
    // 检测拆分出来的两个子多边形是否是凹多边形，若果是，继续递归拆分
    if (isConcavePoly(polygon1)) {
        polygonList = polygonList.concat(divideConcavePoly(polygon1));
    }
    if (isConcavePoly(polygon2)) {
        polygonList = polygonList.concat(divideConcavePoly(polygon2));
    }
    // 返回结果集
    return polygonList;
};
export const creatTrangleFaces = (shape) => {
    const faces = [];
    const startPoint = shape[0];
    for (let i = 1; i < shape.length - 1; i++) {
        faces.push([startPoint, shape[i], shape[i + 1]]);
    }
    return faces;
};
export const creatTrangleFacesByShapes = (shapes) => {
    const faces = [];
    for (let i = 0; i < shapes.length; i++) {
        const shape = shapes[i];
        faces.push(...creatTrangleFaces(shape));
    }
    return faces;
};
